// packages/core/src/learning/schools/anti-scam.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored to match boglehead-indexing.ts (the reference exhibit).
//
// Voice reminder: neutral enough to be trustworthy, opinionated enough to be
// memorable. Frame beliefs as "[School] believes…". Never invent quotes,
// never tell users what to buy, never copy book/creator text.
//
// ID-bound fields (deferred to the post-authoring cross-reference pass):
//   - figures:            [] (none for this school)
//   - featuredLessonIds:  [] until referenced lessons exist
//   - debateIds:          [] until referenced debates exist
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const ANTI_SCAM_EXHIBIT: SchoolExhibitEntry = {
	id: 'anti-scam',
	tagline: 'Anti scam believes pressure is the first warning sign.',
	worldviewSummary:
		'Anti scam is paying attention to manipulation before the money leaves your hands. This lens protects you from urgency, fake authority, emotional pressure, too good to be true offers, and people who want you to act before you think. It accepts a practical tradeoff: being careful can make you slower, more skeptical, and less trusting at first. The blind spot is that suspicion can become fear if you treat every new opportunity or message like a trap instead of learning the warning signs.',
	coreBeliefs: [
		'Anti scam believes pressure is information.',
		'Anti scam believes real opportunities can survive a pause.',
		'Anti scam believes urgency, secrecy, and fear are often used to shut down judgment.',
		'Anti scam believes your future self deserves one calm review before money moves.',
		'Anti scam believes embarrassment is part of the scam design, not proof that the victim is foolish.',
		'Anti scam believes protection starts before you click, reply, send, sign, or transfer.',
	],
	warnsAgainst: [
		'Acting fast because someone made you scared, excited, ashamed, or rushed.',
		'Trusting a message just because it sounds official, personal, or urgent.',
		'Sending money, codes, passwords, or documents before confirming the situation through a separate path.',
		'Believing that being smart, careful, or educated makes you immune to manipulation.',
	],
	helps: 'This lens helps adults who want to protect their money, identity, accounts, documents, and family from manipulation. It may not fit when it turns into fear of every new product, message, or opportunity instead of a calm habit of verification.',
	figures: [],
	canon: [
		{
			title: 'Pause before payment',
			oneLineIdea:
				'A real request can wait long enough for you to verify it calmly.',
		},
		{
			title: 'Verify through another path',
			oneLineIdea:
				'Do not trust the contact method that created the pressure. Confirm it separately.',
		},
		{
			title: 'Urgency is a signal',
			oneLineIdea:
				'Scammers use time pressure because a calm person asks better questions.',
		},
		{
			title: 'Never share the code',
			oneLineIdea:
				'A security code is not proof you are safe. It is often the thing someone is trying to steal.',
		},
		{
			title: 'Too good needs proof',
			oneLineIdea:
				'Big promises deserve more evidence, not faster action.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-anti-scam',
	debateIds: [],
	applyActions: [
		{
			label: 'Check how much one bad hit would change your runway',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Stress test a suspicious money request before acting',
			action: {
				type: 'openSimulator',
				label: 'Simulate the hit',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Look at your current money picture before sending money',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Run a scam pause before you reply',
			action: {
				type: 'noToolAction',
				label: 'Use a scam pause',
				prompt: 'Write what they want, why they say it is urgent, what proof you have, and how you can verify it separately.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'The scam starts before the payment. It starts when someone steals your calm.',
			kind: 'hook',
		},
		{
			line: 'If the offer cannot survive ten minutes of checking, that is the lesson.',
			kind: 'hook',
		},
		{
			line: 'What message, call, or offer almost got you because it felt urgent?',
			kind: 'interview-question',
		},
		{
			line: 'What warning sign do you wish someone taught you before your first expensive mistake?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches adults how to pause before pressure becomes a payment.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial education should include the manipulation tactics that drain real people.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how urgency, secrecy, and fake authority make normal people act fast.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone to break down a scam attempt without blaming the person targeted.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
